/**
 * Buyer-Demand Phase 2 — member-facing custom question prescreen.
 *
 * Reuses the PrescreenModal shell pattern (overlay + card layout) but
 * renders an N-option content body. On submit:
 *   - Posts to /api/manual-prescreen/answer with sessionId + questionHash + answer
 *   - If the answer satisfies `required` (or required is empty),
 *     calls onPass — the parent re-fetches the wall and the survey
 *     should now be entry-eligible
 *   - If not, calls onFail — parent shows a soft "this isn't a fit"
 *     message and routes to /dashboard
 *
 * The required-check is mirrored client-side (no round-trip to know
 * whether the survey will appear) but the server is the source of
 * truth — the wall re-fetch resolves the actual outcome.
 */
import { useState } from 'react';
import { HelpCircle, X, Loader2 } from 'lucide-react';
import { API_BASE } from '../lib/apiBase';
import { supabase } from '../lib/supabase';

interface Props {
  sessionId: string;
  questionHash: string;
  question: string;
  type: 'single' | 'multi';
  options: string[];
  required: string[];
  onPass: () => void;
  onFail: () => void;
  onClose: () => void;
}

export default function CustomQuestionPrescreenModal({
  sessionId,
  questionHash,
  question,
  type,
  options,
  required,
  onPass,
  onFail,
  onClose,
}: Props) {
  const [selected, setSelected] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function toggle(v: string) {
    if (type === 'single') {
      setSelected([v]);
    } else {
      setSelected((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
    }
  }

  async function handleSubmit() {
    setError(null);
    if (selected.length === 0) {
      setError('Please pick at least one option.');
      return;
    }
    setSubmitting(true);

    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Not authenticated');

      const answer = type === 'multi' ? selected.join(',') : selected[0];
      const res = await fetch(`${API_BASE}/api/manual-prescreen/answer`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId, questionHash, answer }),
      });
      if (!res.ok) throw new Error('Failed to save answer');

      // Mirror the server's required-check so we can route the member
      // to the right next state without an extra round-trip. Server is
      // still the source of truth (the wall query reflects it).
      const passes = required.length === 0 || selected.some((s) => required.includes(s));
      if (passes) onPass();
      else onFail();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save answer');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-6 space-y-5">
        <div className="flex items-start justify-between">
          <div className="w-12 h-12 rounded-full bg-[#EEF2FF] flex items-center justify-center">
            <HelpCircle className="w-6 h-6 text-[#5B4FE8]" />
          </div>
          <button onClick={onClose} className="text-[#6B7280] hover:text-[#111827]" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <h3 className="text-lg font-bold text-[#111827]">Quick question</h3>
          <p className="text-sm text-[#6B7280] mt-1">
            Answer this and we'll check if this survey is a fit:
          </p>
        </div>

        <div className="bg-[#F8F9FA] rounded-xl p-4 border border-[#E5E7EB]">
          <p className="text-sm text-[#111827] font-medium">{question}</p>
        </div>

        <div className="space-y-2">
          {options.map((opt) => {
            const active = selected.includes(opt);
            return (
              <button
                key={opt}
                onClick={() => toggle(opt)}
                disabled={submitting}
                className={`w-full px-4 py-3 rounded-xl text-sm font-medium border-2 transition-colors ${
                  active
                    ? 'bg-[#5B4FE8] text-white border-[#5B4FE8]'
                    : 'bg-white text-[#111827] border-[#E5E7EB] hover:border-[#5B4FE8]'
                } disabled:opacity-50`}
              >
                {opt}
              </button>
            );
          })}
        </div>

        {error && <p className="text-sm text-red-500">{error}</p>}

        <button
          onClick={() => void handleSubmit()}
          disabled={submitting || selected.length === 0}
          className="w-full py-3 rounded-full bg-[#5B4FE8] text-sm font-medium text-white hover:bg-indigo-600 disabled:opacity-50 flex items-center justify-center gap-1"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Submit
        </button>
      </div>
    </div>
  );
}
